<template>
  <div class="teams-page">
    <div v-if="!loading" class="teams-page__container">
      <header class="teams-page__header">
        <div class="teams-page__header-top">
          <h1 class="teams-page__title">Team Management</h1>
          <DealRoomTeamInvite
            title="Invite Member"
            @member-added="fetchDashboardData"
            :disabled="!(canPerformAction('team:*') || canPerformAction('team:addMember'))"
          />
        </div>

        <div class="teams-page__stats">
          <div class="teams-page__stat-card">
            <div class="teams-page__stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
              </svg>
            </div>
            <div class="teams-page__stat-info">
              <span class="teams-page__stat-label">Total Roles</span>
              <p class="teams-page__stat-value">{{ totalRoles }}</p>
            </div>
          </div>

          <div class="teams-page__stat-card">
            <div class="teams-page__stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
              </svg>
            </div>
            <div class="teams-page__stat-info">
              <span class="teams-page__stat-label">Team Members</span>
              <div class="teams-page__stat-row">
                <p class="teams-page__stat-value">{{ totalMembers }}</p>
                <div v-if="visibleMembers.length !== 0" class="teams-page__avatars">
                  <div
                    v-for="(member, index) in visibleMembers"
                    :key="member.id"
                    class="teams-page__avatar"
                    :style="{ marginLeft: index !== 0 ? '-10px' : '0' }"
                  >
                    {{ getInitials(member.fname, member.lname) }}
                  </div>
                  <div
                    v-if="hiddenCount > 0"
                    class="teams-page__avatar teams-page__avatar--count"
                    style="margin-left: -8px"
                  >
                    +{{ hiddenCount }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div class="teams-page__content">
        <TabsNav
          :tabs="tabs"
          :active-tab="activeTab"
          @tab-change="handleTabChange"
        >
          <template #action-button>
            <button
              v-if="activeTab === 'roles'"
              @click="navigateToCreateRole"
              class="teams-page__create-btn"
              :disabled="!(canPerformAction('team:*') || canPerformAction('team:addGroup'))"
            >
              <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.75674 6.30357V0.303574H8.93184V6.30357H15.4571V8.30357H8.93184V14.3036H6.75674V8.30357H0.231445V6.30357H6.75674Z" fill="white"/>
              </svg>
              Create Custom Role
            </button>
          </template>
        </TabsNav>

        <div class="teams-page__table-area">
          <RolesTable
            v-if="activeTab === 'roles'"
            :roles="roles"
            :loading="tableLoading"
            @edit-role="handleEditRole"
            @delete-role="handleDeleteRole"
            :onAction="fetchDashboardData"
            :totalPages="totalPages"
            :currentPage="currentPage"
            @gotoPage="gotoPage"
            :isActionAllowed="canPerformAction('team:*') || canPerformAction('team:updateGroup')"
          />

          <MembersTable
            v-else-if="activeTab === 'members'"
            :members="members"
            :loading="tableLoading"
            @edit-role="handleEditRole"
            @delete-role="handleDeleteRole"
            :isActionAllowed="canPerformAction('team:*') || canPerformAction('team:updateMember')"
          />

          <div v-else-if="activeTab === 'activity'">
            <p>Activity</p>
          </div>

          <EmptyState v-if="shouldShowEmptyState">
            <template #message>
              <div class="teams-page__empty-message">
                <p v-if="activeTab === 'roles' && !roles.length">
                  You currently do not have any roles defined
                </p>
                <p v-if="activeTab === 'members' && !members.length">
                  You currently do not have any team members in your dealroom
                </p>
                <p v-if="activeTab === 'activity' && !activities.length">
                  No activity has been recorded yet
                </p>
              </div>
            </template>
          </EmptyState>
        </div>
      </div>
    </div>

    <Loader v-if="loading" />
  </div>
</template>


<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useGetters, useActions } from "@/composables/useMapper";

import RolesTable from "@/components/teams/tables/RolesTable.vue";
import MembersTable from "@/components/teams/tables/MembersTable.vue";
import TabsNav from "@/components/teams/TabsNav.vue";
import EmptyState from "@/components/notification/EmptyState";
import DealRoomTeamInvite from "@/components/teams/DealRoomTeamInvite";
import { permissionsService } from "@/utils/permissionChecker.js"


const router = useRouter();
const store = useStore(); 

const { teamMembers, iamGroups } = useGetters('organisationModule', [
  'teamMembers',
  'iamGroups'
]);

const { 
  getIAMGroups, 
  getTeamMembers, 
  getTeamMember, 
  deleteRole 
} = useActions('organisationModule', [
  'getIAMGroups',
  'getTeamMembers',
  'getTeamMember',
  'deleteRole'
]);

const { showToast } = useActions('', ['showToast']);

const loading = ref(false);
const tableLoading = ref(false);
const activities = ref([]);
const activeTab = ref("roles");
const visibleMembers = ref([]);
const hiddenCount = ref(0);

const roles = computed(() => iamGroups.value?.docs || []);
const totalRoles = computed(() => iamGroups.value?.totalDocs || 0);
const totalPages = computed(() => iamGroups.value?.totalPages || 0);
const currentPage = computed(() => iamGroups.value?.page || 0);
const members = computed(() => teamMembers.value || []);
const totalMembers = computed(() => teamMembers.value?.length || 0);

// Checking permissions for user
const permissions = ref(null);

permissions.value = permissionsService.loadUserPermissions();

const canPerformAction = (action) => {
  return permissionsService.hasPermission(action);
};

const getInitials = (fname, lname) => {
  return `${fname?.charAt(0) ?? ""}${lname?.charAt(0) ?? ""}`.toUpperCase();
};

const tabs = [
  { id: "roles", label: "Roles" },
  { id: "members", label: "Members" },
  // { id: "activity", label: "Activity Log" },
];

const shouldShowEmptyState = computed(() => {
  if (activeTab.value === "roles")
    return !roles.value.length && !tableLoading.value;
  if (activeTab.value === "members")
    return !members.value.length && !tableLoading.value;
  if (activeTab.value === "activity")
    return !activities.value.length && !tableLoading.value;
  return false;
});

const fetchDashboardData = async () => {
  loading.value = true;
  try {
    await Promise.all([
      getIAMGroups(),
      getTeamMembers({ page: 1 }),
    ]);
    
    visibleMembers.value = members.value.length > 5
      ? members.value.slice(0, 5)
      : members.value;
    
    hiddenCount.value = members.value.length > 5
      ? Math.max(0, members.value.length - 5)
      : 0;

    fetchTeamMembersForRoles();
  } catch (error) {
    console.log(error);
  } finally {
    loading.value = false;
  }
};


const gotoPage = async (page) => {
  loading.value = true;
  try {
    await getTeamMembers({ page });
  } catch (error) {
    console.log(error);
  } finally {
    loading.value = false;
  }
};

const fetchRoles = async () => {
  tableLoading.value = true;
  try {
    await getIAMGroups();

    fetchTeamMembersForRoles();
  } catch (error) {
    console.log(error);
  } finally {
    tableLoading.value = false;
  }
};

const fetchTeamMembersForRoles = async () => {

  const localRoles = [...roles.value];
  
  for (const role of localRoles) {
    try {
      const response = await getTeamMember(role.id);
      role.teamMembers = response.data.data.docs || [];

    } catch (error) {
      console.log(`Failed to fetch team members for role ${role.id}:`, error);
      role.teamMembers = [];
    }
  }
  
  store.commit('organisationModule/SET_IAMGROUPS_WITH_MEMBERS', localRoles);
};

const fetchMembers = async () => {
  tableLoading.value = true;
  try {
    await getTeamMembers({ page: 1 });
  } catch (error) {
    console.log(error);
  } finally {
    tableLoading.value = false;
  }
};

const handleTabChange = (tabId) => {
  activeTab.value = tabId;

  if (tabId === "roles") {
    fetchRoles();
  } else if (tabId === "members") {
    fetchMembers();
  } else if (tabId === "activity") {
    // fetchActivityLog();
  }
};

const navigateToCreateRole = () => {
  router.push("/dashboard/teams/create-role");
};

const handleEditRole = (roleId) => {
  router.push(`/dashboard/teams/edit-role/${roleId}`);
};

const handleDeleteRole = async (roleId) => {
  try {
    const res = await deleteRole(roleId);

    if (res) {
      showToast({
        message: "Role successfully deleted",
        type: "success",
      });
    }

    fetchRoles();
  } catch (error) {
    console.log(error);
  }
};

onMounted(() => {
  fetchDashboardData();
});

watch(
  () => router.currentRoute.value.query.tab,
  (newTabQuery) => {
    if (newTabQuery && tabs.some((tab) => tab.id === newTabQuery)) {
      activeTab.value = newTabQuery;
      handleTabChange(newTabQuery);
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-page: #F8FAFC;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$btn-primary: #001240;
$btn-primary-hover: #06206B;

.teams-page {
  width: 100%;
  min-height: 100vh;
  background: $bg-page;

  &__container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 24px 16px;

    @media screen and (min-width: 1024px) {
      padding: 32px 24px;
    }
  }

  &__header {
    margin-bottom: 32px;
  }

  &__header-top {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;

    @media screen and (min-width: 640px) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  }

  &__title {
    font-size: 24px;
    font-weight: 600;
    color: $text-primary;
    margin: 0;
    letter-spacing: -0.02em;
  }

  &__stats {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;

    @media screen and (min-width: 640px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  &__stat-card {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    background: $bg-card;
    border: 1px solid $border-color;
    border-radius: 12px;
    padding: 24px;
  }

  &__stat-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: $bg-subtle;
    border-radius: 12px;
    color: $text-secondary;
    flex-shrink: 0;
  }

  &__stat-info {
    flex: 1;
    min-width: 0;
  }

  &__stat-label {
    display: block;
    font-size: 13px;
    color: $text-muted;
    margin-bottom: 8px;
  }

  &__stat-value {
    font-size: 32px;
    font-weight: 600;
    color: $text-primary;
    margin: 0;
    line-height: 1;
  }

  &__stat-row {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__avatars {
    display: flex;
    align-items: center;
  }

  &__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 500;
    background: $bg-subtle;
    color: $text-secondary;
    border: 2px solid $bg-card;

    &--count {
      background: transparent;
      border: none;
      color: $text-muted;
      font-size: 13px;
      font-weight: 500;
    }
  }

  &__content {
    background: $bg-card;
    border: 1px solid $border-color;
    border-radius: 10px;
    overflow: hidden;
  }

  &__create-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: $btn-primary;
    color: white;
    border: none;
    border-radius: 7px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover:not(:disabled) {
      background: $btn-primary-hover;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__table-area {
    padding: 20px;
  }

  &__empty-message {
    text-align: center;
    color: $text-muted;
    padding: 24px 0;
  }
}
</style>